import * as Sentry from '@sentry/vue'
import { sanitizeLoggedUrl } from '@/utils/sanitizeUrl'

/**
 * Normalize an unknown thrown/rejected value into an Error with a useful message.
 * API clients often reject with plain error-shaped objects, not Error instances, so an
 * uncaught rejection's `.reason` is often such an object — stringifying it directly
 * yields the useless "[object Object]".
 */
export function toLoggableError(reason: unknown): Error {
  if (reason instanceof Error) return reason
  if (reason && typeof reason === 'object') {
    const message = (reason as Record<string, unknown>).message
    if (typeof message === 'string' && message) {
      return new Error(message)
    }
    try {
      return new Error(JSON.stringify(reason))
    } catch {
      // falls through to String(reason) below
    }
  }
  return new Error(String(reason))
}

interface LogContext {
  user?: {
    id: number
  }
  tenant_id?: number | null
  route?: string
  action?: string
  timestamp: string
  userAgent: string
  url: string
}

class LoggerService {
  private async getContext(action?: string): Promise<LogContext> {
    const user = await this.getUserInfo()
    const hasWindow = typeof window !== 'undefined'
    return {
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      url: hasWindow ? sanitizeLoggedUrl(window.location.href) : '',
      route: hasWindow ? window.location.pathname : '',
      action,
      ...(user ?? {})
    }
  }

  private async getUserInfo(): Promise<Pick<LogContext, 'user' | 'tenant_id'> | null> {
    try {
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()
      if (!authStore.user) {
        return null
      }
      return {
        user: { id: authStore.user.id },
        tenant_id: authStore.tenantId
      }
    } catch {
      return null
    }
  }

  private shouldReportToSentry(action?: string): boolean {
    if (!action) {
      return true
    }
    return (
      action !== 'api_server_error' &&
      action !== 'api_unknown_error' &&
      action !== 'api_network_error'
    )
  }

  private captureToSentry(error: Error, context: LogContext): void {
    if (!import.meta.env.VITE_SENTRY_DSN || !this.shouldReportToSentry(context.action)) {
      return
    }

    try {
      const tags: Record<string, string> = {}
      if (context.action) {
        tags.action = context.action
      }
      if (context.route) {
        tags.route = context.route
      }
      if (context.tenant_id != null) {
        tags.tenant_id = String(context.tenant_id)
      }

      Sentry.captureException(error, {
        tags,
        extra: {
          timestamp: context.timestamp,
          url: context.url,
          user_agent: context.userAgent
        },
        ...(context.user ? { user: { id: String(context.user.id) } } : {})
      })
    } catch (sentryError) {
      console.error('Sentry failed to capture exception:', sentryError, 'Original error:', error)
    }
  }

  async logError(error: Error, context?: Partial<LogContext>): Promise<void> {
    const logContext = { ...(await this.getContext(context?.action)), ...context }

    if (import.meta.env.DEV) {
      console.error('Error logged:', { message: error.message, stack: error.stack, ...logContext })
    }

    this.captureToSentry(error, logContext)
  }

  logWarning(message: string, context?: Partial<LogContext>): void {
    if (import.meta.env.DEV) {
      console.warn('Warning:', message, context)
    }
  }
}

export const loggerService = new LoggerService()
